import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosInstance";
import { API } from "../config/config";
import { Snackbar, Alert } from "@mui/material";
import {
  FaSpinner,
  FaUserShield,
  FaLock,
  FaUsers,
  FaCog,
  FaCheck,
  FaTimes,
} from "react-icons/fa";

const Roles = ({ roleId = null }) => {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [slug, setSlug] = useState("");
  const [roleName, setRoleName] = useState("");
  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [error, setError] = useState("");
  const [popup, setPopup] = useState({
    open: false,
    message: "",
    severity: "",
  });

  useEffect(() => {
    const fetchRolesAndModules = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("adminToken");

        // Fetch roles
        const rolesResponse = await axiosInstance.get(API.ADMIN.ROLES_LIST, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoles(rolesResponse.data.roles);

        // Fetch modules
        const moduleResponse = await axiosInstance.get(API.ADMIN.MODULES_LIST, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const modulesData = moduleResponse.data.modules;

        // Initialize permissions
        const permissionsFromModules = modulesData.reduce((acc, module) => {
          acc[module._id] = {
            add: false,
            edit: false,
            view: false,
            delete: false,
          };
          return acc;
        }, {});

        setModules(modulesData);

        // If a role is selected, fetch its details
        if (roleId) {
          const roleResponse = await axiosInstance.get(
            `${API.ADMIN.ROLE_DETAILS}/${roleId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const roleData = roleResponse.data;

          setSlug(roleData.slug);
          setRoleName(roleData.name);

          // Update permissions with role-specific actions
          const updatedPermissions = modulesData.reduce((acc, module) => {
            const rolePermission = roleData.permissions.find(
              (perm) => perm.module._id === module._id
            );
            acc[module._id] = rolePermission
              ? rolePermission.actions
              : { add: false, edit: false, view: false, delete: false };
            return acc;
          }, {});

          setPermissions(updatedPermissions);
        } else {
          setPermissions(permissionsFromModules);
        }
      } catch (error) {
        setError("Error fetching roles or modules.");
        console.error("Error fetching roles or modules:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRolesAndModules();
  }, [roleId]);

  const handleRoleChange = async (selectedRoleId) => {
    setSelectedRoleId(selectedRoleId);

    if (selectedRoleId) {
      try {
        setRoleLoading(true);
        setError("");
        const token = localStorage.getItem("adminToken");

        // Fetch role details
        const roleResponse = await axiosInstance.get(
          `${API.ADMIN.ROLE_DETAILS}/${selectedRoleId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const roleData = roleResponse.data;
        console.log(roleData);
        setSlug(roleData.slug);
        setRoleName(roleData.name);
        // Update permissions based on role details
        const updatedPermissions = modules.reduce((acc, module) => {
          const rolePermission = roleData.permissions.find(
            (perm) => perm.module._id === module._id
          );
          acc[module._id] = rolePermission
            ? rolePermission.actions
            : { add: false, edit: false, view: false, delete: false };
          return acc;
        }, {});

        setPermissions(updatedPermissions);
      } catch (error) {
        setError("Error fetching role details.");
        console.error("Error fetching role details:", error);
      } finally {
        setRoleLoading(false);
      }
    } else {
      // Reset permissions to default if no role is selected
      const defaultPermissions = modules.reduce((acc, module) => {
        acc[module._id] = {
          add: false,
          edit: false,
          view: false,
          delete: false,
        };
        return acc;
      }, {});
      setPermissions(defaultPermissions);
      setSlug("");
      setRoleName("");
    }
  };

  const handleCheckboxChange = (moduleId, action) => {
    setPermissions((prevState) => ({
      ...prevState,
      [moduleId]: {
        ...prevState[moduleId],
        [action]: !prevState[moduleId][action],
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!slug) {
      setError("Role slug is required.");
      return;
    }

    console.log(permissions);

    const roleData = {
      name: roleName, // Add this
      slug,
      permissions: Object.keys(permissions).map((moduleId) => ({
        module: moduleId,
        actions: permissions[moduleId],
      })),
    };

    try {
      const token = localStorage.getItem("adminToken");
      await axiosInstance.post(API.ADMIN.CREATEROLEPERMISSION, roleData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPopup({
        open: true,
        message: "Permissions saved successfully!",
        severity: "success",
      });
    } catch (error) {
      setError("Error saving permissions.");
      console.error("Error saving permissions:", error);
    }
  };

  const handleClosePopup = () => {
    setPopup({ ...popup, open: false });
  };

  // Show loading state for the entire roles page
  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 bg-gray-50 min-h-screen">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUserShield className="text-6xl text-purple-500 mb-4 mx-auto" />
            <FaSpinner className="animate-spin text-3xl text-purple-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Loading Roles & Permissions
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we fetch roles and module data...
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <FaUserShield className="text-3xl text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-800">Role Management</h1>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <FaLock className="text-purple-500" />
          <span>Permissions & Access Control</span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <FaTimes className="text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Role Selection Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center mb-4">
          <FaUsers className="text-purple-500 mr-2" />
          <h2 className="text-xl font-semibold text-gray-800">Select Role</h2>
          {roleLoading && (
            <div className="ml-auto flex items-center text-purple-600">
              <FaSpinner className="animate-spin mr-2" />
              <span className="text-sm">Loading role details...</span>
            </div>
          )}
        </div>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose a role to manage permissions:
          </label>
          <div className="relative">
            <select
              onChange={(e) => handleRoleChange(e.target.value)}
              value={selectedRoleId}
              disabled={roleLoading}
              className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                roleLoading ? "bg-gray-100 cursor-not-allowed opacity-75" : ""
              }`}
            >
              <option value="">Select a Role</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
            {roleLoading && (
              <div className="absolute inset-y-0 right-10 flex items-center">
                <FaSpinner className="animate-spin text-purple-500" />
              </div>
            )}
          </div>
        </div>

        {/* Role Loading Card */}
        {roleLoading && (
          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <FaSpinner className="animate-spin text-purple-500 mr-3" />
              <div>
                <p className="text-purple-700 font-medium">
                  Loading Role Details
                </p>
                <p className="text-purple-600 text-sm">
                  Fetching permissions and settings...
                </p>
              </div>
            </div>
            <div className="mt-3 bg-purple-200 rounded-full h-1 overflow-hidden">
              <div
                className="bg-purple-500 h-1 rounded-full animate-pulse"
                style={{ width: "60%" }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Permissions Form Section - Only show when a role is selected */}
      {selectedRoleId && (
        <form onSubmit={handleSubmit}>
          <div
            className={`bg-white rounded-lg shadow-md p-6 relative ${
              roleLoading ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            {/* Loading Overlay */}
            {roleLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-90 rounded-lg flex items-center justify-center z-10">
                <div className="text-center">
                  <FaSpinner className="animate-spin text-4xl text-purple-500 mb-3 mx-auto" />
                  <p className="text-purple-700 font-medium">
                    Updating Permissions
                  </p>
                  <p className="text-purple-600 text-sm">Please wait...</p>
                </div>
              </div>
            )}

            <div className="flex items-center mb-6">
              <FaLock className="text-purple-500 mr-2" />
              <h2 className="text-xl font-bold text-gray-800">
                Module Permissions
              </h2>
            </div>

            <div className="space-y-6">
              {modules.map((module) => (
                <div
                  key={module._id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                >
                  <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center">
                    <FaCog className="text-gray-500 mr-2" />
                    {module.name.charAt(0).toUpperCase() +
                      module.name.slice(1)}{" "}
                    Module
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {["add", "edit", "view", "delete"].map((action) => {
                      const isChecked =
                        permissions[module._id]?.[action] || false;
                      const actionColors = {
                        add: "text-green-600",
                        edit: "text-blue-600",
                        view: "text-gray-600",
                        delete: "text-red-600",
                      };
                      const actionIcons = {
                        add: "➕",
                        edit: "✏️",
                        view: "👁️",
                        delete: "🗑️",
                      };

                      return (
                        <label
                          key={action}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() =>
                              handleCheckboxChange(module._id, action)
                            }
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="flex items-center gap-2">
                            <span>{actionIcons[action]}</span>
                            <span
                              className={`font-medium ${actionColors[action]}`}
                            >
                              {action.charAt(0).toUpperCase() + action.slice(1)}
                            </span>
                          </span>
                          {isChecked && (
                            <FaCheck className="text-green-500 ml-auto" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Save Button */}
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 focus:ring-4 focus:ring-purple-300 transition-all duration-200 flex items-center gap-2"
              >
                <FaCheck className="text-sm" />
                Save Permissions
              </button>
            </div>
          </div>
          {/* Snackbar Popup */}
          <Snackbar
            open={popup.open}
            autoHideDuration={6000}
            onClose={handleClosePopup}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleClosePopup}
              severity={popup.severity}
              sx={{ width: "100%" }}
            >
              {popup.message}
            </Alert>
          </Snackbar>
        </form>
      )}

      {/* Empty State - Show when no role is selected */}
      {!selectedRoleId && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FaUserShield className="text-6xl mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No Role Selected
            </h3>
            <p className="text-gray-500">
              Please select a role from the dropdown above to view and manage
              its permissions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
